import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

import Axios from '../../../networking/intraceptor';
import {
	pending_quantity_change_popup_reasons,
	save_quantity_change_popup_reason,
} from '../../../networking/urlEndPoints';
import { useCustomSelectors } from '../../../services/selectors/allSelectors';
import { safeToast } from '../../../utils/helper/helper';
import { useCustomLocation } from '../../../hooks';
import * as routeNames from '../../../routes/routeName';
import {
	DELIVERED_RX_REASON_SNOOZE_OPTIONS,
	getAppTimeLocalValue,
	getDeliveredRxReasonSnoozeUntilDate,
} from './DeliveredRxReasonChangeReviewShared';

const REFRESH_EVENT = 'quantity-change-reasons:refresh';
const SOCKET_EVENT = 'rx-quantity-change-reasons-saved';
const SNOOZE_STORAGE_PREFIX = 'quantityChangeReasonReviewSnooze';

const getSnoozeStorageKey = (pharmacyId, userId) =>
	`${SNOOZE_STORAGE_PREFIX}:${pharmacyId || 'pharmacy'}:${userId || 'user'}`;

const getStoredSnoozeUntilMs = (storageKey) => {
	try {
		const storedValue = localStorage.getItem(storageKey);
		if (!storedValue) return null;

		const parsedValue = JSON.parse(storedValue);
		const snoozeUntilMs = new Date(parsedValue?.snoozeUntil || parsedValue).getTime();
		if (Number.isNaN(snoozeUntilMs) || snoozeUntilMs <= Date.now()) {
			localStorage.removeItem(storageKey);
			return null;
		}

		return snoozeUntilMs;
	} catch (_) {
		localStorage.removeItem(storageKey);
		return null;
	}
};

const getRecordFormKey = (record, index = 0) => String(record?.id || record?.record_key || `${record?.rx_number || 'rx'}-${record?.rxrefill_id || index}-${index}`);

const getRecordIdentityKey = (record) => {
	if (record?.id) {
		return `id:${record.id}`;
	}

	return [
		record?.rx_number || record?.rxnumber || 'rx',
		record?.rxrefill_id || record?.refill_id || '',
		record?.reef_date || '',
		record?.add_datetime || '',
	].join('|');
};

const normalizeQuantityChangeRecord = (record) => {
	const rxNumber = record?.rx_number || record?.rxnumber || '';
	const rxRefillId = record?.rxrefill_id || record?.refill_id || '';

	return {
		...record,
		rx_number: rxNumber,
		rxrefill_id: rxRefillId,
		record_key: record?.record_key || getRecordIdentityKey({ ...record, rx_number: rxNumber, rxrefill_id: rxRefillId }),
		reason: record?.reason || '',
	};
};

const mergeQuantityChangeRecords = (currentRecords, incomingRecords) => {
	const merged = new Map();

	currentRecords.forEach((record) => {
		merged.set(getRecordIdentityKey(record), record);
	});

	incomingRecords.forEach((record) => {
		merged.set(getRecordIdentityKey(record), record);
	});

	return Array.from(merged.values());
};

const removeQuantityChangeRecords = (currentRecords, acknowledgedRecords) => {
	const acknowledgedKeys = new Set(acknowledgedRecords.map(getRecordIdentityKey));

	return currentRecords.filter((record) => !acknowledgedKeys.has(getRecordIdentityKey(record)));
};

const QuantityChangeReasonModal = ({
	records,
	totalCount,
	reasons,
	errors,
	submitting,
	onReasonChange,
	onSubmit,
	onSkip,
}) => {
	const [snoozeOption, setSnoozeOption] = useState(DELIVERED_RX_REASON_SNOOZE_OPTIONS[0].value);
	const [customSnoozeTime, setCustomSnoozeTime] = useState(() =>
		getAppTimeLocalValue(new Date(Date.now() + 60 * 60 * 1000))
	);
	const [isSkipMenuOpen, setIsSkipMenuOpen] = useState(false);

	if (!records.length) return null;

	const isCustomSnooze = snoozeOption === 'custom';
	const isSkipDisabled = submitting || (isCustomSnooze && !customSnoozeTime);
	const handleSkipClick = (snoozeConfig) => {
		onSkip({
			minutes: snoozeConfig?.minutes,
			customTime: snoozeConfig?.customTime,
		});
		setIsSkipMenuOpen(false);
	};

	return (
		<div className="fixed inset-0 z-[170] flex items-center justify-center bg-black/50 px-4 py-6">
			<div className="w-full max-w-7xl max-h-[90vh] overflow-visible rounded-xl border border-gray-200 bg-white shadow-xl">
				<div className="border-b border-gray-200 bg-gradient-to-r from-gray-100 to-gray-100 px-5 py-4">
					<div className="flex flex-wrap items-center gap-2">
						<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-700">
							<ReportProblemOutlinedIcon fontSize="small" />
						</span>
						<h2 className="text-lg font-semibold text-gray-900">Quantity Change Review</h2>
						{totalCount > 1 && (
							<span className="rounded-md bg-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-700">
								{totalCount} pending
							</span>
						)}
					</div>
				</div>

				<div className="max-h-[62vh] overflow-auto">
					<table className="min-w-[1180px] table-auto border-collapse border border-gray-200 text-left text-sm">
						<thead className="sticky top-0 z-10 bg-gray-50">
							<tr>
								<th className="w-[5%] border border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-700">
									<span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-xs font-bold">#</span>
								</th>
								<th className="w-[15%] border border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-700">Patient</th>
								<th className="w-[10%] border border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-700">Rx</th>
								<th className="w-[12%] border border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-700">Refill Date</th>
								<th className="w-[15%] whitespace-nowrap border border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-700">Updated Date Time</th>
								<th className="w-[16%] whitespace-nowrap border border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-700">Delivery Date Time</th>
								<th className="w-[10%] border border-gray-200 px-4 py-2 text-center text-xs font-semibold uppercase tracking-wider text-gray-700">Prev Qty</th>
								<th className="w-[8%] border border-gray-200 px-4 py-2 text-center text-xs font-semibold uppercase tracking-wider text-gray-700">QTY</th>
								<th className="min-w-[320px] border border-gray-200 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-700">Reason</th>
							</tr>
						</thead>
						<tbody className="bg-white">
							{records.map((record, index) => {
								const recordKey = getRecordFormKey(record, index);
								const patientLabel = record.patient_name || '';
								const rxLabel = record.rx_number || '-';
								const error = errors[recordKey];

								return (
									<tr key={recordKey} className="align-top transition-colors hover:bg-gray-50">
										<td className="border border-gray-200 px-4 py-3">
											<span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-800">
												{index + 1}
											</span>
										</td>
										<td className="border border-gray-200 px-4 py-3">
											<span className="text-sm font-semibold text-gray-900">{patientLabel}</span>
										</td>
										<td className="border border-gray-200 px-4 py-3">
											<span className="text-sm font-semibold text-gray-900">{rxLabel}</span>
										</td>
										<td className="border border-gray-200 px-4 py-3">
											<span className="text-sm font-semibold text-gray-900">{record.formatted_reef_date || record.reef_date || '-'}</span>
										</td>
										<td className="whitespace-nowrap border border-gray-200 px-4 py-3">
											<span className="text-sm font-medium text-gray-700">{record.add_datetime_display || record.add_datetime || '-'}</span>
										</td>
										<td className="whitespace-nowrap border border-gray-200 px-4 py-3">
											<span className="text-sm font-medium text-gray-700">{record.delivery_datetime_display || record.delivery_datetime || '-'}</span>
										</td>
										<td className="border border-gray-200 px-4 py-3 text-center">
											<span className="text-sm font-bold text-red-700">{record.previous_qty ?? '-'}</span>
										</td>
										<td className="border border-gray-200 px-4 py-3 text-center">
											<span className="text-sm font-bold text-green-700">{record.updated_qty ?? '-'}</span>
										</td>
										<td className="border border-gray-200 px-4 py-3">
											<input
												id={`quantity-change-reason-${recordKey}`}
												type="text"
												value={reasons[recordKey] || ''}
												onChange={(event) => onReasonChange(recordKey, event.target.value)}
												disabled={submitting}
												className={`h-10 w-full rounded-md border px-3 py-2 text-sm text-gray-900 outline-none transition ${
													error
														? 'border-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-500'
														: 'border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
												} ${submitting ? 'cursor-not-allowed bg-slate-100' : 'bg-white'}`}
												placeholder="Enter reason"
											/>
											{error && <p className="mt-2 text-sm text-red-600">{error}</p>}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>

				<div className="relative z-30 flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
					<div className="relative">
						<button
							type="button"
							onClick={() => setIsSkipMenuOpen((isOpen) => !isOpen)}
							disabled={submitting}
							className={`relative z-10 border px-5 py-2 text-sm font-semibold transition ${
								submitting
									? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500'
									: 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
							} ${isSkipMenuOpen ? 'rounded-b-md rounded-t-none border-t-white' : 'rounded-md'}`}
						>
							Skip
						</button>
						{isSkipMenuOpen && (
							<div className="absolute bottom-full right-0 z-50 mb-px max-h-[min(60vh,360px)] w-52 overflow-auto rounded-md rounded-br-none border border-gray-300 bg-white shadow-lg">
								{DELIVERED_RX_REASON_SNOOZE_OPTIONS.filter((option) => option.value !== 'custom').map((option) => (
									<button
										key={option.value}
										type="button"
										onClick={() => handleSkipClick({ minutes: option.minutes })}
										className="block w-full px-4 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
									>
										{option.label}
									</button>
								))}
								<div className="border-t border-gray-200 px-3 py-2">
									<button
										type="button"
										onClick={() => setSnoozeOption('custom')}
										className={`${snoozeOption === 'custom' ? 'mb-2' : ''} block w-full text-left text-sm font-semibold text-gray-700`}
									>
										Custom
									</button>
									{snoozeOption === 'custom' && (
										<div className="flex items-center gap-2">
											<input
												type="time"
												value={customSnoozeTime}
												onChange={(event) => setCustomSnoozeTime(event.target.value)}
												className="h-9 min-w-0 flex-1 rounded-md border border-gray-300 px-2 text-sm font-medium text-gray-800 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
											/>
											<button
												type="button"
												onClick={() => handleSkipClick({ customTime: customSnoozeTime })}
												disabled={isSkipDisabled}
												className={`h-9 rounded-md px-3 text-sm font-semibold transition ${
													isSkipDisabled
														? 'cursor-not-allowed bg-slate-100 text-slate-500'
														: 'bg-blue-600 text-white hover:bg-blue-700'
												}`}
											>
												Set
											</button>
										</div>
									)}
								</div>
							</div>
						)}
					</div>
					<button
						type="button"
						onClick={onSubmit}
						disabled={submitting}
						className={`rounded-md px-5 py-2 text-sm font-semibold transition ${
							submitting
								? 'cursor-not-allowed bg-slate-200 text-slate-600'
								: 'bg-blue-600 text-white hover:bg-blue-700'
						}`}
					>
						{submitting ? 'Saving...' : 'Continue'}
					</button>
				</div>
			</div>
		</div>
	);
};

const QuantityChangeReasonPopupManager = () => {
	const { pharmacyId, profileData, teamStatus } = useCustomSelectors();
	const location = useCustomLocation();
	const [records, setRecords] = useState([]);
	const [reasons, setReasons] = useState({});
	const [fieldErrors, setFieldErrors] = useState({});
	const [submitting, setSubmitting] = useState(false);
	const pendingLoadRequestRef = useRef(null);

	const isPharmacist = String(profileData?.type || '').toLowerCase() === 'pharmacist';
	const isTeamUserRestricted = process.env.NODE_ENV === 'production' && Number(teamStatus) === 1;
	const isInvestigationPage = location.pathname === routeNames.pharmacy_investigate_rx;
	const snoozeStorageKey = getSnoozeStorageKey(pharmacyId, profileData?.user_id);
	const [snoozeUntilMs, setSnoozeUntilMs] = useState(() => getStoredSnoozeUntilMs(snoozeStorageKey));

	const isSnoozed = useCallback(() => {
		const storedSnoozeUntilMs = getStoredSnoozeUntilMs(snoozeStorageKey);
		setSnoozeUntilMs(storedSnoozeUntilMs);
		return Boolean(storedSnoozeUntilMs);
	}, [snoozeStorageKey]);

	const loadPendingRecords = useCallback(async () => {
		if (!pharmacyId || !profileData?.user_id || !isPharmacist || isTeamUserRestricted || isInvestigationPage) {
			setRecords([]);
			return;
		}

		if (isSnoozed()) {
			setRecords([]);
			return;
		}

		if (pendingLoadRequestRef.current) {
			return pendingLoadRequestRef.current;
		}

		const loadRequest = Axios.get(pending_quantity_change_popup_reasons);
		pendingLoadRequestRef.current = loadRequest;

		try {
			const response = await loadRequest;
			const responseData = response?.data?.data || {};
			const nextRecords = Array.isArray(responseData.records)
				? responseData.records.map(normalizeQuantityChangeRecord)
				: [];
			const shouldShowPopup = responseData.show_popup ?? nextRecords.length > 0;
			setRecords(shouldShowPopup && !isSnoozed() ? nextRecords : []);
		} catch (error) {
			if (error?.response?.status !== 401 && process.env.NODE_ENV === 'development') {
				console.warn('Unable to load quantity changes.', error);
			}
		} finally {
			if (pendingLoadRequestRef.current === loadRequest) {
				pendingLoadRequestRef.current = null;
			}
		}
	}, [isInvestigationPage, isPharmacist, isSnoozed, isTeamUserRestricted, pharmacyId, profileData?.user_id]);

	useEffect(() => {
		loadPendingRecords();

		const handleRefresh = () => loadPendingRecords();

		window.addEventListener(REFRESH_EVENT, handleRefresh);

		return () => {
			window.removeEventListener(REFRESH_EVENT, handleRefresh);
		};
	}, [loadPendingRecords]);

	useEffect(() => {
		if (!pharmacyId || !profileData?.user_id || !snoozeUntilMs) {
			return undefined;
		}

		const delay = snoozeUntilMs - Date.now();
		if (delay <= 0) {
			localStorage.removeItem(snoozeStorageKey);
			setSnoozeUntilMs(null);
			loadPendingRecords();
			return undefined;
		}

		const timer = setTimeout(() => {
			localStorage.removeItem(snoozeStorageKey);
			setSnoozeUntilMs(null);
			loadPendingRecords();
		}, delay);

		return () => clearTimeout(timer);
	}, [loadPendingRecords, pharmacyId, profileData?.user_id, snoozeStorageKey, snoozeUntilMs]);

	useEffect(() => {
		setReasons((previousReasons) => {
			const nextReasons = {};
			records.forEach((record, index) => {
				const recordKey = getRecordFormKey(record, index);
				nextReasons[recordKey] = previousReasons[recordKey] ?? record.reason ?? '';
			});

			return nextReasons;
		});
		setFieldErrors({});
	}, [records]);

	useEffect(() => {
		if (!pharmacyId || !profileData?.user_id || !isPharmacist || isTeamUserRestricted) {
			setRecords([]);
			return undefined;
		}

		const handleSocketEvent = (event) => {
			const detail = event?.detail || {};
			if (detail.event !== SOCKET_EVENT) {
				return;
			}

			if (isSnoozed()) {
				setRecords([]);
				return;
			}

			const payload = Array.isArray(detail.payload) ? detail.payload[0] : detail.payload || {};
			const incomingRecords = Array.isArray(payload?.records)
				? payload.records.map(normalizeQuantityChangeRecord)
				: [];
			const eventPharmacyId = Number(payload?.pharmacy_id);
			if (eventPharmacyId && Number(pharmacyId) && eventPharmacyId !== Number(pharmacyId)) {
				return;
			}

			if (!incomingRecords.length) {
				loadPendingRecords();
				return;
			}

			if (payload?.action === 'acknowledged') {
				setRecords((currentRecords) => removeQuantityChangeRecords(currentRecords, incomingRecords));
				return;
			}

			setRecords((currentRecords) => mergeQuantityChangeRecords(currentRecords, incomingRecords));
		};

		window.addEventListener('oatrx:realtime:event', handleSocketEvent);

		return () => {
			window.removeEventListener('oatrx:realtime:event', handleSocketEvent);
		};
	}, [isPharmacist, isSnoozed, isTeamUserRestricted, loadPendingRecords, pharmacyId, profileData?.user_id]);

	if (isTeamUserRestricted) {
		return null;
	}

	const handleSubmit = async () => {
		if (!records.length) return;

		const nextErrors = {};
		const normalizedReasons = records.reduce((accumulator, record, index) => {
			const recordKey = getRecordFormKey(record, index);
			const normalizedReason = (reasons[recordKey] || '').trim();
			accumulator[recordKey] = normalizedReason;

			if (!normalizedReason) {
				nextErrors[recordKey] = 'Reason is required.';
			}

			return accumulator;
		}, {});

		if (Object.keys(nextErrors).length) {
			setFieldErrors(nextErrors);
			return;
		}

		setSubmitting(true);
		setFieldErrors({});

		try {
			await Axios.post(save_quantity_change_popup_reason, {
				records: records.map((record, index) => ({
					id: record.id,
					rx_number: record.rx_number,
					rxrefill_id: record.rxrefill_id || '',
					winrx_data_id: record.winrx_data_id,
					patient_id: record.patient_id,
					reef_date: record.reef_date,
					add_datetime: record.add_datetime,
					previous_qty: record.previous_qty,
					updated_qty: record.updated_qty,
					reason: normalizedReasons[getRecordFormKey(record, index)],
				})),
			});

			setRecords([]);
			setReasons({});
			safeToast.success('Quantity change reason saved.');
			loadPendingRecords();
		} catch (error) {
			const errors = error?.response?.data?.data?.error;
			const reasonError = errors?.['records.0.reason']?.[0];
			setFieldErrors(reasonError && records.length ? { [getRecordFormKey(records[0], 0)]: reasonError } : {});
			safeToast.error(error?.response?.data?.message || 'Unable to save quantity change reason.');
		} finally {
			setSubmitting(false);
		}
	};

	const handleSkip = (snoozeConfig = {}) => {
		const snoozeUntilDate = getDeliveredRxReasonSnoozeUntilDate(snoozeConfig);
		const snoozeUntilTime = snoozeUntilDate?.getTime();

		if (!snoozeUntilDate || Number.isNaN(snoozeUntilTime) || snoozeUntilTime <= Date.now()) {
			return;
		}

		localStorage.setItem(snoozeStorageKey, JSON.stringify({
			snoozeUntil: snoozeUntilDate.toISOString(),
		}));
		setSnoozeUntilMs(snoozeUntilTime);
		setRecords([]);
		setReasons({});
		setFieldErrors({});
	};

	return (
		<QuantityChangeReasonModal
			records={records}
			totalCount={records.length}
			reasons={reasons}
			errors={fieldErrors}
			submitting={submitting}
			onReasonChange={(recordKey, value) => {
				setReasons((previous) => ({ ...previous, [recordKey]: value }));
				if (fieldErrors[recordKey]) {
					setFieldErrors((previous) => {
						const nextErrors = { ...previous };
						delete nextErrors[recordKey];
						return nextErrors;
					});
				}
			}}
			onSubmit={handleSubmit}
			onSkip={handleSkip}
		/>
	);
};

export { REFRESH_EVENT as QUANTITY_CHANGE_REASONS_REFRESH_EVENT };
export default QuantityChangeReasonPopupManager;
